"use client"

import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useRouter } from "next/navigation"
import { useAuth } from "../context/AuthContext"
import Logo from "./Logo"
import LanguageSwitcher from "./LanguageSwitcher"
import { trackEvent } from "../lib/analytics"

const TopMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isClient, setIsClient] = useState(false)
  const { t } = useTranslation("TopMenu")
  const router = useRouter()
  const { user } = useAuth()

  useEffect(() => {
    setIsClient(true)
  }, [])

  const handleLogin = useCallback(async () => {
    if (user) {
      router.push("/app")
      return
    }

    try {
      const { supabase } = await import("../lib/supabase")
      const redirectTo = `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      })
      if (error) {
        console.error("Google login error:", error.message)
        alert("An unexpected error occurred during login. Please try again.")
      }
    } catch (err) {
      console.error("Login error:", err)
      alert("An unexpected error occurred during login. Please try again.")
    }
  }, [user, router])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
    setIsOpen(false)
  }

  const handleContactCreator = () => {
    window.open("https://wa.me/+4552803868", "_blank", "noopener,noreferrer")
    setIsOpen(false)
  }

  const navigationItems = [
    { key: "benefits", label: "Benefits", action: () => scrollToSection("benefits-section") },
    { key: "testimonials", label: "Testimonials", action: () => scrollToSection("testimonials-section") },
    { key: "faq", label: "FAQ", action: () => scrollToSection("faq-section") },
    { key: "contact", label: "Contact", action: handleContactCreator },
  ]

  return (
    <nav className="w-full bg-white/95 backdrop-blur-sm border-b border-slate-200/60 h-16 flex items-center z-50 sticky top-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center w-full">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="text-lg font-semibold text-slate-900 tracking-tight">
            Roset
          </span>
        </div>

        {/* Nav links */}
        <div className="hidden md:flex items-center gap-7">
          {navigationItems.map((item) => (
            <button
              key={item.key}
              onClick={item.action}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors duration-150"
            >
              {isClient ? t(item.key) : item.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2.5">
          {!user && (
            <button
              onClick={() => { trackEvent("cta_click", { location: "nav" }); router.push("/app") }}
              className="hidden sm:inline-flex px-4 py-1.5 text-sm font-semibold rounded-full bg-blue-600 text-white hover:bg-blue-500 transition duration-150"
            >
              {isClient ? t("getStarted") || "Get Started Free" : "Get Started Free"}
            </button>
          )}
          <button
            onClick={handleLogin}
            className="px-4 py-1.5 text-sm font-medium rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition duration-150"
          >
            {isClient ? (user ? t("openApp") : t("login")) : "Log in"}
          </button>
          <LanguageSwitcher />

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden flex flex-col justify-center items-center w-8 h-8 gap-1"
          >
            <span className={`w-5 h-0.5 bg-slate-600 transition-all duration-200 ${isOpen ? "rotate-45 translate-y-1.5" : ""}`} />
            <span className={`w-5 h-0.5 bg-slate-600 transition-all duration-200 ${isOpen ? "opacity-0" : ""}`} />
            <span className={`w-5 h-0.5 bg-slate-600 transition-all duration-200 ${isOpen ? "-rotate-45 -translate-y-1.5" : ""}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                onClick={item.action}
                className="block w-full text-left text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium py-2.5 px-3 rounded-lg text-sm transition-colors duration-150"
              >
                {isClient ? t(item.key) : item.label}
              </button>
            ))}
            {!user && (
              <button
                onClick={() => { trackEvent("cta_click", { location: "nav_mobile" }); router.push("/app"); setIsOpen(false) }}
                className="block w-full text-center bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-full transition duration-150 mt-2"
              >
                {isClient ? t("getStarted") || "Get Started Free" : "Get Started Free"}
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

export default TopMenu
